import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BrowseTypesEnum,
  Coordinate,
  ScenicPlayerComponent,
  Top3,
  TrainingSection,
  PlayerService,
  ApiHttpResponse,
} from '@kinomap-dev/ng-ui-lib';
import { VideoSection } from '@kinomap-dev/ng-ui-lib';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VideoApi } from 'app/modules/browse/api/video.api';
import { StravaSection } from 'app/modules/dashboard/models/stravaSection.model';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  Observable,
  of,
  shareReplay,
  Subscription,
} from 'rxjs';
import { GenericViewComponent } from '../../components/generic-view/generic-view.component';
import { ActivityApi } from 'app/modules/dashboard/api/activity.api';
import { User } from 'app/models';
import { Store } from '@ngrx/store';
import { selectProfileData } from 'app/store/me/me.selectors';
import { FavoriteApi } from '../../api/favorite.api';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from 'app/services/toaster.service';

@Component({
  imports: [
    RippleModule,
    TranslateModule,
    SkeletonModule,
    AsyncPipe,
    GenericViewComponent,
    ScenicPlayerComponent,
  ],
  selector: 'app-watch',
  standalone: true,
  styleUrls: ['./watch.component.scss'],
  templateUrl: './watch.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchComponent implements OnInit, OnDestroy {
  public readonly translate: TranslateService = inject(TranslateService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  public videoApi: VideoApi = inject(VideoApi);
  private activityApi: ActivityApi = inject(ActivityApi);
  private toasterService: ToasterService = inject(ToasterService);
  private favoriteService: FavoriteApi = inject(FavoriteApi);
  private store: Store = inject(Store);
  private playerService: PlayerService = inject(PlayerService);

  @ViewChild('childsSectionsCaroussel') childsSectionsCarousselRef!: ElementRef;
  @ViewChild('stravaSectionsBlock') stravaSectionsBlockRef!: ElementRef;
  @ViewChild(GenericViewComponent) genericViewComponent?: GenericViewComponent;

  editLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  client$: Observable<User> = this.store.select(selectProfileData);
  videoSection$: Observable<VideoSection> = new Observable<VideoSection>();
  trainingSections$: BehaviorSubject<TrainingSection[]> = new BehaviorSubject<
    TrainingSection[]
  >([]);
  coordinates$: BehaviorSubject<Coordinate[]> = new BehaviorSubject<
    Coordinate[]
  >([]);
  comments$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  stravaSections$: Observable<StravaSection[]> = new Observable<
    StravaSection[]
  >();
  top3$: Observable<Top3> = new Observable<Top3>();
  videoId: number;
  subscriptions: Subscription[] = [];
  thumbnailLoading: boolean = false;
  isSyncMode: Observable<boolean> =
    this.playerService.getSyncMode() || of(false);
  isSyncLoading: boolean = false;

  getComments(): void {
    this.subscriptions.push(
      this.videoApi
        .getComments(this.videoId)
        .subscribe((comments: Comment[]) => this.comments$.next(comments))
    );
  }

  getCoordinates(): void {
    this.subscriptions.push(
      this.videoApi
        .getVideoCoordinates(this.videoId)
        .subscribe((coordinate: Coordinate[]) =>
          this.coordinates$.next(coordinate)
        )
    );
  }

  getTrainingSections(): void {
    this.subscriptions.push(
      this.videoApi
        .getTrainingSections(this.videoId)
        .subscribe((trainingSections: TrainingSection[]) =>
          this.trainingSections$.next(trainingSections)
        )
    );
  }

  getTop3(): void {
    this.top3$ = this.activityApi.getActivityTop(this.videoId);
  }

  changeScenicData(scenicId: number, formData: FormData): void {
    this.subscriptions.push(
      this.videoApi.postEditScenicVideo(scenicId, formData).subscribe({
        next: (response: ApiHttpResponse<VideoSection>) => {
          this.videoSection$ = of(response.data);
          this.editLoading$.next(false);
          this.toasterService.success('', 'Successfully changed');
          if (this.genericViewComponent?.isEditable) {
            this.genericViewComponent.toggleEditMode();
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error editing scenic video:', error);
          this.editLoading$.next(false);
          this.toasterService.error(null, 'Error updating video data');
        },
      })
    );
  }

  onDownloadGPX(): void {
    this.subscriptions.push(
      this.videoApi.getVideoGPX(this.videoId).subscribe((res: Blob) => {
        const blob: Blob = new Blob([res], {
          type: 'application/octet-stream',
        });
        const link: HTMLAnchorElement = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `kinomap-${this.videoId}.gpx`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      })
    );
  }

  onLikeClick(videoSection: VideoSection): void {
    const likeAction: Observable<{ data: { likes_count: number } }> =
      videoSection.has_liked
        ? this.videoApi.deleteVideoLike(this.videoId)
        : this.videoApi.addVideoLike(this.videoId);
    this.subscriptions.push(
      likeAction.subscribe(({ data: { likes_count } }) => {
        videoSection.likes_count = likes_count;
        this.videoSection$ = of(videoSection);
      })
    );
  }

  onSyncClick({
    syncCoordinate,
    videoCoordinate,
  }: {
    syncCoordinate: Coordinate;
    videoCoordinate: Coordinate;
  }): void {
    const startTime: number =
      syncCoordinate?.coo_time - videoCoordinate?.position * 1000;

    if (this.isSyncLoading) {
      this.toasterService.warning(
        null,
        this.translate.instant('watch.map_player.sync_send_progress')
      );
      return;
    }

    if (startTime) {
      this.toasterService.success(
        null,
        this.translate.instant('watch.map_player.sync_send_start')
      );
      this.isSyncLoading = true;
      this.videoApi.editVideoSync(this.videoId, startTime).subscribe({
        next: (): void => {
          this.isSyncMode = of(false);
          this.isSyncLoading = false;
          this.toasterService.success(
            null,
            this.translate.instant('watch.map_player.sync_send_success')
          );
        },
        error: (): void => {
          this.isSyncLoading = false;
          this.toasterService.error(
            null,
            this.translate.instant('watch.map_player.sync_send_error')
          );
        },
      });
    }
  }

  onFavoriteClick(video: VideoSection): void {
    const favoriteAction: Observable<void> = video.is_favorite
      ? this.favoriteService.deleteFavorite$(
          this.videoId,
          BrowseTypesEnum.Video
        )
      : this.favoriteService.postFavorite$(this.videoId, BrowseTypesEnum.Video);

    video.is_favorite = !video.is_favorite;
    this.videoSection$ = of(video);
    this.subscriptions.push(favoriteAction.subscribe());
  }

  onThumbnailEditClick(
    event: { paused: boolean; time: number },
    id: string
  ): void {
    if (this.thumbnailLoading) {
      this.toasterService.warning(
        null,
        this.translate.instant(
          'watch.video_player.thumbnail_update_processing_error'
        )
      );
      return;
    }

    if (!event.paused) {
      this.toasterService.error(
        null,
        this.translate.instant('watch.video_player.thumbnail_not_paused_error')
      );
      return;
    }

    this.thumbnailLoading = true;

    this.toasterService.info(
      null,
      this.translate.instant('watch.video_player.thumbnail_update_processing')
    );

    this.videoApi.updateThumbnail(id, event.time, 'watch').subscribe({
      complete: (): void => {
        this.toasterService.success(
          null,
          this.translate.instant('watch.video_player.thumbnail_change_success')
        );
        this.thumbnailLoading = false;
      },
      error: (): void => {
        this.toasterService.error(
          null,
          this.translate.instant('watch.video_player.thumbnail_change_error')
        );
        this.thumbnailLoading = false;
      },
    });
  }

  // TrackBy functions pour optimiser les performances des listes
  trackByVideoId(index: number, item: any): number {
    return item?.id || index;
  }

  trackByCommentId(index: number, comment: any): number {
    return comment?.id || index;
  }

  trackBySectionId(index: number, section: any): number {
    return section?.id || index;
  }

  ngOnInit(): void {
    this.videoId = this.activatedRoute.snapshot.params['videoId'];
    this.videoSection$ = this.videoApi
      .getVideoInformation(this.videoId)
      .pipe(shareReplay(1));
    this.stravaSections$ = this.videoApi
      .getStravaSections(this.videoId)
      .pipe(shareReplay(1));
    this.getTrainingSections();
    this.getCoordinates();
    this.comments$.next([]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];

    // Nettoyage des BehaviorSubjects
    this.editLoading$.complete();
    this.trainingSections$.complete();
    this.coordinates$.complete();
    this.comments$.complete();
  }
}
